import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  Skeleton,
  TextField,
  Tooltip,
} from "@mui/material";
import useOauthApplications from "hooks/api/useOauthApplications";
import theme from "lib/theme";
import { Link as RouterLink } from "react-router-dom";
import { OauthApplication } from "types/oauthApplication";
import { useState } from "react";
import { Cancel, Code, ContentCopy, Delete } from "@mui/icons-material";
import PlanFeatureWrapper from "components/PlanFeatureWrapper";
import PaywallModal from "components/PaywallModal";
import { formatDate } from "utils/helpers";
import { Modal } from "components/common/Modal";
import useCurrentUserPermission from "hooks/useCurrentUserPermission";

interface ApiTableProps {
  oauthApplications: OauthApplication[];
  deleteOauthApplications: (uid: string) => Promise<void>;
}

interface ApiRowProps {
  key: string;
  oauthApplication: OauthApplication;
  deleteOauthApplications: (uid: string) => Promise<void>;
}

function ApiTable() {
  const { oauthApplications, isLoading, deleteOauthApplications } = useOauthApplications();
  return (
    <div>
      {isLoading ? (
        <>
          <Skeleton variant="text" height={75} />
          <Skeleton variant="text" height={75} />
          <Skeleton variant="text" height={75} />
          <Skeleton variant="text" height={75} />
          <Skeleton variant="text" height={75} />
        </>
      ) : oauthApplications && oauthApplications.length > 0 ? (
        <ApiDataTable oauthApplications={oauthApplications} deleteOauthApplications={deleteOauthApplications} />
      ) : (
        <EmptyTable />
      )}
    </div>
  );
}

function CreateAppButton() {
  const { hasPermission } = useCurrentUserPermission();
  return (
    <Tooltip
      title={
        hasPermission("apiCredentials")
          ? ""
          : "You do not have permissions for this action, please contact your administrator for permission."
      }
    >
      <span>
        <PlanFeatureWrapper
          feature="api"
          enabledElement={
            <Button
              component={RouterLink}
              variant="contained"
              size="large"
              to="/api/credentials/new"
              disableElevation
              disabled={!hasPermission("apiCredentials")}
            >
              Create Application
            </Button>
          }
          disabledElement={
            <>
              <PaywallModal
                feature={"apiTable"}
                trigger={(props) => (
                  <Button variant="contained" size="large" {...props} disableElevation>
                    Create Application
                  </Button>
                )}
              />
            </>
          }
          grandfatheredElement={
            <Button
              component={RouterLink}
              variant="contained"
              size="large"
              to="/api/credentials/new"
              disableElevation
              disabled={!hasPermission("apiCredentials")}
            >
              Create Application
            </Button>
          }
        />
      </span>
    </Tooltip>
  );
}

function EmptyTable() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" height={400}>
      <Box maxWidth={390} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
        <Code sx={{ fontSize: 70, opacity: 0.25 }} />
        <Typography variant="h5">You don’t have any applications yet!</Typography>
        <Typography mt={2} mb={3} textAlign="center">
          Integrate with Revivn's API to seamlessly manage your pickups, assets, and access your data.
        </Typography>
        <CreateAppButton />
      </Box>
    </Box>
  );
}

function ApiDataTable({ oauthApplications, deleteOauthApplications }: ApiTableProps) {
  return (
    <>
      <TableContainer
        sx={{
          mt: 2,
        }}
      >
        <Table sx={{ ".MuiTableRow-root:last-child td, .MuiTableRow-root:last-child th": { border: 0 } }}>
          <TableHead sx={{ ".MuiTableCell-root": { color: theme.palette.text.disabled, borderBottom: 0 } }}>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Created</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {oauthApplications?.map((oauthApplication) => {
              return (
                <ApiTableRow
                  key={oauthApplication.id}
                  oauthApplication={oauthApplication}
                  deleteOauthApplications={deleteOauthApplications}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CreateAppButton />
    </>
  );
}

function ApiTableRow({ oauthApplication, deleteOauthApplications }: ApiRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { hasPermission } = useCurrentUserPermission();
  return (
    <>
      <TableRow>
        <TableCell>{oauthApplication.name}</TableCell>
        <TableCell>
          <TextField
            fullWidth
            size="small"
            variant="standard"
            value={oauthApplication.uid}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "monospace",
                fontSize: "14px",
              },
              endAdornment: (
                <Tooltip placement="top" title="Copy">
                  <IconButton onClick={() => navigator.clipboard.writeText(oauthApplication.uid)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
        </TableCell>
        <TableCell>{formatDate(oauthApplication.createdAt)}</TableCell>
        <TableCell>
          <Tooltip
            placement="top"
            title={
              hasPermission("apiCredentials")
                ? "Delete"
                : "You do not have permissions for this action, please contact your administrator for permission."
            }
          >
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={!hasPermission("apiCredentials")}
                onClick={() => setIsOpen(true)}
              >
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      <Modal open={isOpen} setOpen={setIsOpen}>
        <Box sx={{ textAlign: "center" }}>
          <Cancel color="primary" sx={{ fontSize: "60px" }} />

          <Typography fontWeight="bold" fontSize="24px" mt={1}>
            Delete Oauth Application
          </Typography>

          <Typography mt={0.5} fontSize="20px">
            Are you sure you want to delete this oauth application?
          </Typography>

          <Box mt={5} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                deleteOauthApplications(oauthApplication.uid).then(() => {
                  setIsOpen(false);
                });
              }}
            >
              Yes, delete
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              no, don’t Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default ApiTable;
